import dynamic from 'next/dynamic'
import { useCallback, useState } from 'react'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import {
  GetMoreBestsellers,
  GetMoreNewProducts,
  GetMoreCampaignProducts,
  GetMorePopularAccessories,
} from 'src/graphql/get-category-for-landing-page.graphql'
import type {
  GetMoreBestsellersQuery,
  GetMoreBestsellersQueryVariables,
  GetMoreCampaignProductsQuery,
  GetMoreCampaignProductsQueryVariables,
  GetMoreNewProductsQuery,
  GetMoreNewProductsQueryVariables,
  GetMorePopularAccessoriesQuery,
  GetMorePopularAccessoriesQueryVariables,
} from 'src/graphql/generated/graphql-types'
import type { BannersType, ProductReferenceListType } from '../product-lists'
import type { CategoryType } from '.'
import ProductPanel from './product-panel'

// Elevate 3 banners will be removed in future releases
const BannerHero = dynamic(() => import('../ads').then(mod => mod.BannerHero))

const Carousel = dynamic(() => import('@dustin-web/microsite-components/src/carousel-test/carousel').then(mod => mod.CarouselTest))
const BannerflowBanner = dynamic(() => import('@dustin-web/microsite-components/src/bannerflow-banner').then(mod => mod.BannerflowBanner))

type Props = {
  categoryId: string
  category: CategoryType
}

const CategoryPanels = ({ categoryId, category }: Props) => {
  const { translate } = useTranslations()
  const { isSmallScreen } = useResponsive()

  const getMoreVariables = {
    variables: {
      categoryId,
    },
  }

  const [skipMoreBestsellers, setSkipMoreBestsellers] = useState(true)
  const { data: getMoreBestsellersData, isValidating: getMoreBestsellersLoading } = useGraphql<
    GetMoreBestsellersQuery,
    GetMoreBestsellersQueryVariables
  >(GetMoreBestsellers, {
    ...getMoreVariables,
    context: {
      skip: skipMoreBestsellers,
    },
  })

  const [skipMoreNewProducts, setSkipMoreNewProducts] = useState(true)
  const { data: getMoreNewProductsData, isValidating: getMoreNewProductsLoading } = useGraphql<
    GetMoreNewProductsQuery,
    GetMoreNewProductsQueryVariables
  >(GetMoreNewProducts, {
    ...getMoreVariables,
    context: {
      skip: skipMoreNewProducts,
    },
  })

  const [skipMoreCampaignProducts, setSkipMoreCampaignProducts] = useState(true)
  const { data: getMoreCampaignProductsData, isValidating: getMoreCampaignProductsLoading } =
    useGraphql<GetMoreCampaignProductsQuery, GetMoreCampaignProductsQueryVariables>(
      GetMoreCampaignProducts,
      {
        ...getMoreVariables,
        context: {
          skip: skipMoreCampaignProducts,
        },
      }
    )

  const [skipMorePopularAccessories, setSkipMorePopularAccessories] = useState(true)
  const { data: getMorePopularAccessoriesData, isValidating: getMorePopularAccessoriesLoading } =
    useGraphql<GetMorePopularAccessoriesQuery, GetMorePopularAccessoriesQueryVariables>(
      GetMorePopularAccessories,
      {
        ...getMoreVariables,
        context: {
          skip: skipMorePopularAccessories,
        },
      }
    )

  const getAvailableProductsForSale = useCallback(
    (listType: 'bestsellers' | 'newProducts' | 'campaignProducts' | 'popularAccessories') => availableProductsForSale(listType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category]
  )

  const availableProductsForSale = (listType: 'bestsellers' | 'newProducts' | 'campaignProducts' | 'popularAccessories') => {
    return {
      ...(category?.productLists?.[listType] ?? {}),
      productReferences: category?.productLists?.[listType]?.productReferences.filter(product => product?.searchResultProduct?.availability?.isAvailableForSale)
    } as ProductReferenceListType
  }

  const getMoreAvailableProductsForSale = (products: ProductReferenceListType['productReferences'] | undefined) => {
    return products?.filter(product => product?.searchResultProduct?.availability?.isAvailableForSale)
  }

  // Elevate 3 banners will be removed in future releases
  const ads = category?.productLists?.ads.filter(ad => !ad.banner?.url?.includes('script')) || []

  const categoryBanners = category?.banners?.filter((banner: BannersType ) => !!banner?.url && !!banner.productImageOverrideUrl) ?? []
  const bestSellers = getAvailableProductsForSale('bestsellers')
  const newProducts = getAvailableProductsForSale('newProducts')
  const campaignProducts = getAvailableProductsForSale('campaignProducts')
  const popularAccessories = getAvailableProductsForSale('popularAccessories')

  return category?.productLists ? (
    <>
      {bestSellers.productReferences.length > 0 && (
        <ProductPanel
          productReferenceList={bestSellers}
          title={translate('Panel_Bestsellers_Title')}
          showLessText={translate('Panel_Bestsellers_ShowLess')}
          showMoreText={translate('Panel_Bestsellers_ShowMore')}
          showMoreQuery={{
            getMore: () => setSkipMoreBestsellers(false),
            loadingMore: getMoreBestsellersLoading,
            moreProductReferences:
              getMoreAvailableProductsForSale(getMoreBestsellersData?.category?.productLists?.bestsellers.productReferences),
          }}
          digitalDataOrigin="categoryPage"
        />
      )}
      {
        // Fallback to Elevate 3 banners will be removed in future releases
        ads.length > 0 && categoryBanners.length === 0 && <BannerHero ads={ads} />
      }
      {categoryBanners.length > 0 &&
        <div className="container u-pt-32">
          <Carousel
            arrows={false}
            dots={categoryBanners.length > 1}
            dotMode={isSmallScreen && 'full-width' || 'right'}
            type='startpageBanners'
            list={
              categoryBanners?.map((banner, index) =>
                <div key={index} className='u-px-8'>
                  <BannerflowBanner
                    script={banner?.url!}
                    scriptMobile={banner?.productImageOverrideUrl!}
                  />
                </div>
              )
            } />
        </div>
      }
      {newProducts.productReferences.length > 0 && (
        <ProductPanel
          productReferenceList={newProducts}
          title={translate('Panel_NewProducts_Title')}
          showLessText={translate('Panel_NewProducts_ShowLess')}
          showMoreText={translate('Panel_NewProducts_ShowMore')}
          showMoreQuery={{
            getMore: () => setSkipMoreNewProducts(false),
            loadingMore: getMoreNewProductsLoading,
            moreProductReferences:
              getMoreAvailableProductsForSale(getMoreNewProductsData?.category?.productLists?.newProducts.productReferences),
          }}
          digitalDataOrigin="categoryPage"
        />
      )}
      {campaignProducts.productReferences.length > 0 && (
        <ProductPanel
          productReferenceList={campaignProducts}
          title={translate('Panel_CampaignProducts_Title')}
          showLessText={translate('Panel_CampaignProducts_ShowLess')}
          showMoreText={translate('Panel_CampaignProducts_ShowMore')}
          showMoreQuery={{
            getMore: () => setSkipMoreCampaignProducts(false),
            loadingMore: getMoreCampaignProductsLoading,
            moreProductReferences:
              getMoreAvailableProductsForSale(getMoreCampaignProductsData?.category?.productLists?.campaignProducts.productReferences)
          }}
          digitalDataOrigin="categoryPage"
        />
      )}
      {popularAccessories.productReferences.length > 0 && (
        <ProductPanel
          productReferenceList={popularAccessories}
          title={translate('Panel_PopularAccessories_Title')}
          showLessText={translate('Panel_PopularAccessories_ShowLess')}
          showMoreText={translate('Panel_PopularAccessories_ShowMore')}
          showMoreQuery={{
            getMore: () => setSkipMorePopularAccessories(false),
            loadingMore: getMorePopularAccessoriesLoading,
            moreProductReferences:
              getMoreAvailableProductsForSale(getMorePopularAccessoriesData?.category?.productLists?.popularAccessories
                .productReferences)
          }}
          digitalDataOrigin="categoryPage"
        />
      )}
    </>
  ) : null
}

export default CategoryPanels
